// The ONE sortable table of the app (fleet frontend standard 1): header click
// sorts on the RAW value (never the formatted string), numeric columns start
// descending, the second click reverses, the sorted header carries aria-sort,
// rows highlight on hover (theme token), and any column can carry a help icon
// whose explanation opens in a row below the header.
import { Fragment, useMemo, useState, type ReactNode } from 'react';
import { compareDecimal, isDecimalString } from '../lib/decimal';
import { HelpButton, HelpText } from './Help';

export type SortValue = string | number | null | undefined;

export interface Column<T> {
  key: string;
  label: ReactNode;
  /** Plain-text label for aria (defaults to key). */
  ariaLabel?: string;
  numeric?: boolean;
  /** Raw value to sort on. Omit to make the column unsortable. */
  sortValue?: (row: T) => SortValue;
  render: (row: T) => ReactNode;
  help?: string;
  wrap?: boolean;
  /** Hide the label visually (checkbox column). */
  srOnly?: boolean;
  width?: string;
}

export interface SortState {
  key: string;
  dir: 'asc' | 'desc';
}

interface TableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  defaultSort?: SortState;
  rowClass?: (row: T) => string | undefined;
  empty?: ReactNode;
  footer?: ReactNode;
  label: string;
  /** Tighter cell padding for tables with many columns. */
  dense?: boolean;
  /**
   * Server-side sorting (a paged table): the caller owns the sort state and the rows arrive
   * already ordered; a header click calls `onSortChange` instead of sorting the page.
   */
  sort?: SortState | null;
  onSortChange?: (s: SortState) => void;
  /** Expandable rows: when `expanded(row)` is true, `detail(row)` renders in a full-width row right below it. */
  expanded?: (row: T) => boolean;
  detail?: (row: T) => ReactNode;
}

const collator = new Intl.Collator('hu');

export function compareValues(a: SortValue, b: SortValue, numeric: boolean): number {
  const aNull = a === null || a === undefined;
  const bNull = b === null || b === undefined;
  if (aNull || bNull) return aNull === bNull ? 0 : aNull ? 1 : -1; // nulls handled by the caller
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  const sa = String(a);
  const sb = String(b);
  if (numeric && isDecimalString(sa) && isDecimalString(sb)) return compareDecimal(sa, sb);
  return collator.compare(sa, sb);
}

export function sortRows<T>(rows: T[], col: Column<T> | undefined, dir: 'asc' | 'desc'): T[] {
  if (!col?.sortValue) return rows;
  const get = col.sortValue;
  const sign = dir === 'asc' ? 1 : -1;
  return [...rows].sort((x, y) => {
    const a = get(x);
    const b = get(y);
    const aNull = a === null || a === undefined;
    const bNull = b === null || b === undefined;
    if (aNull || bNull) return aNull === bNull ? 0 : aNull ? 1 : -1; // missing values always last
    return sign * compareValues(a, b, !!col.numeric);
  });
}

export function Table<T>({ columns, rows, rowKey, defaultSort, rowClass, empty, footer, label, dense, sort: controlled, onSortChange, expanded, detail }: TableProps<T>) {
  const [localSort, setSort] = useState<SortState | null>(defaultSort ?? null);
  const serverSide = onSortChange !== undefined;
  const sort = serverSide ? (controlled ?? null) : localSort;
  const [helpOpen, setHelpOpen] = useState<string | null>(null);

  const sorted = useMemo(() => {
    if (!sort || serverSide) return rows;
    return sortRows(rows, columns.find((c) => c.key === sort.key), sort.dir);
  }, [rows, columns, sort, serverSide]);

  const onSort = (col: Column<T>) => {
    if (!col.sortValue) return;
    const next: SortState = sort && sort.key === col.key ? { key: col.key, dir: sort.dir === 'asc' ? 'desc' : 'asc' } : { key: col.key, dir: col.numeric ? 'desc' : 'asc' };
    if (serverSide) onSortChange(next);
    else setSort(next);
  };

  const openHelpCol = columns.find((c) => c.key === helpOpen);

  return (
    <div className="table-wrap">
      <table className={`tbl${dense ? ' dense' : ''}`} aria-label={label}>
        <thead>
          <tr>
            {columns.map((c) => {
              const isSorted = sort?.key === c.key;
              const aria = isSorted ? (sort?.dir === 'asc' ? 'ascending' : 'descending') : undefined;
              return (
                <th key={c.key} className={`${c.numeric ? 'num' : ''}${isSorted ? ' sorted' : ''}`} aria-sort={aria} style={c.width ? { width: c.width } : undefined}>
                  {c.srOnly ? (
                    <span className="sr-only">{c.label}</span>
                  ) : c.sortValue ? (
                    <button type="button" className="thbtn" onClick={() => onSort(c)} data-sort-key={c.key}>
                      <span>{c.label}</span>
                      <span className="sort" aria-hidden="true">
                        {isSorted ? (sort?.dir === 'asc' ? '↑' : '↓') : '↕'}
                      </span>
                    </button>
                  ) : (
                    <span>{c.label}</span>
                  )}
                  {c.help ? (
                    <HelpButton small label={c.ariaLabel ?? c.key} open={helpOpen === c.key} onToggle={() => setHelpOpen((o) => (o === c.key ? null : c.key))} />
                  ) : null}
                </th>
              );
            })}
          </tr>
          {openHelpCol?.help ? (
            <tr className="help-row">
              <td colSpan={columns.length}>
                <HelpText helpKey={openHelpCol.help} />
              </td>
            </tr>
          ) : null}
        </thead>
        <tbody>
          {sorted.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="tbl-empty">
                {empty ?? 'Nincs megjeleníthető sor.'}
              </td>
            </tr>
          ) : (
            sorted.map((row) => (
              <Fragment key={rowKey(row)}>
                <tr className={rowClass?.(row)}>
                  {columns.map((c) => (
                    <Fragment key={c.key}>
                      <td className={`${c.numeric ? 'num' : ''}${c.wrap ? ' wrap' : ''}`}>{c.render(row)}</td>
                    </Fragment>
                  ))}
                </tr>
                {expanded?.(row) && detail ? (
                  <tr className="detail-row">
                    <td colSpan={columns.length}>{detail(row)}</td>
                  </tr>
                ) : null}
              </Fragment>
            ))
          )}
        </tbody>
        {footer ? <tfoot>{footer}</tfoot> : null}
      </table>
    </div>
  );
}
